import { RULES } from '@urlcade/contracts';
export { RULES } from '@urlcade/contracts';
export function createMovementState(spawn) { return { ...spawn, vx: 0, vy: 0, grounded: false, airJumps: 1, coyoteMs: 0, jumpBufferMs: 0, dashMs: 0, dashCooldownMs: 0, facing: 1 }; }
export function bodyBounds(s) { return { x: s.x - RULES.bodyWidth / 2, y: s.y - RULES.bodyHeight, width: RULES.bodyWidth, height: RULES.bodyHeight }; }
export function intersects(a, b) { return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y; }
export function containsPoint(r, p) { return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height; }
export function distance(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
/** Deterministic, already-separated placements that satisfy the runtime's hide-seek geometry. */
export function decoyPlacementCandidates(map) {
    if (map.setup.mode !== 'site_hide_seek' || map.propTemplates.length === 0)
        return [];
    const maxWidth = Math.max(...map.propTemplates.map(t => t.displayWidth));
    const maxHeight = Math.max(...map.propTemplates.map(t => t.displayHeight));
    const raw = [];
    const keys = new Set();
    for (const zone of map.setup.decoyPlacementZones)
        for (const surfaceCollider of map.colliders) {
            const surface = surfaceCollider.bounds;
            if (surface.y - maxHeight < zone.y || surface.y > zone.y + zone.height)
                continue;
            const min = Math.max(surface.x + RULES.bodyWidth / 2, zone.x + maxWidth / 2, maxWidth / 2);
            const max = Math.min(surface.x + surface.width - RULES.bodyWidth / 2, zone.x + zone.width - maxWidth / 2, map.world.width - maxWidth / 2);
            if (min > max)
                continue;
            const count = Math.floor((max - min) / (maxWidth + 12)) + 1;
            for (let index = 0; index < count; index++) {
                const x = count === 1 ? (min + max) / 2 : min + index * (max - min) / (count - 1);
                const position = { x, y: surface.y };
                const key = `${x.toFixed(4)}:${surface.y.toFixed(4)}`;
                if (keys.has(key))
                    continue;
                keys.add(key);
                const body = bodyBounds(position);
                const jitterSafeBody = { x: body.x - 5, y: body.y, width: body.width + 10, height: body.height };
                if (map.colliders.some(c => c.kind === 'solid' && intersects(jitterSafeBody, c.bounds)))
                    continue;
                raw.push(position);
            }
        }
    const padded = (point) => ({ x: point.x - maxWidth / 2 - 4, y: point.y - maxHeight - 4, width: maxWidth + 8, height: maxHeight + 8 });
    const plain = (point) => ({ x: point.x - maxWidth / 2, y: point.y - maxHeight, width: maxWidth, height: maxHeight });
    const candidates = [];
    for (const position of raw) {
        if (candidates.some(other => intersects(padded(position), plain(other)) || intersects(padded(other), plain(position))))
            continue;
        candidates.push(position);
    }
    return candidates;
}
export function countSeparatedDecoySlots(map) { return decoyPlacementCandidates(map).length; }
export function minimumDecoyPlacementSlots(map) {
    const desired = map.creatorPatch?.mode === 'site_hide_seek' ? map.creatorPatch.decoyCount : RULES.decoyCount;
    return Math.min(8, desired);
}
export function stepMovement(state, input, map, dtMs = RULES.tickMs, options = {}) {
    return stepMovementWithSolids(state, input, map, dtMs, options, map.colliders);
}
// One physics implementation serves runtime and reachability. A reachability
// query owns an unchanged local collider list, so its solid subset can be
// selected once in original order rather than rediscovered on every frame.
function stepMovementWithSolids(state, input, map, dtMs, options, horizontalColliders, reuseOwnedState = false, verticalCandidates) {
    // Runtime/prediction retain copy-on-write. Only platformReachable passes its
    // private, non-escaping state for reuse; arithmetic and collider order are shared.
    // Inputs never control dt on server; prediction uses exactly the same fixed-step function.
    const s = reuseOwnedState ? state : { ...state };
    const ms = Math.max(0, Math.min(dtMs, RULES.tickMs));
    const dt = ms / 1000;
    s.dashCooldownMs = Math.max(0, s.dashCooldownMs - ms);
    s.dashMs = Math.max(0, s.dashMs - ms);
    s.coyoteMs = s.grounded ? RULES.coyoteMs : Math.max(0, s.coyoteMs - ms);
    s.jumpBufferMs = input.jumpPressed ? RULES.jumpBufferMs : Math.max(0, s.jumpBufferMs - ms);
    if (input.axisX !== 0)
        s.facing = input.axisX;
    if (s.jumpBufferMs > 0 && (s.grounded || s.coyoteMs > 0 || s.airJumps > 0)) {
        if (!s.grounded && s.coyoteMs <= 0)
            s.airJumps--;
        s.vy = RULES.jumpSpeed;
        s.grounded = false;
        s.coyoteMs = 0;
        s.jumpBufferMs = 0;
        s.dashMs = 0;
    }
    if (options.dashAllowed !== false && (options.mode === undefined || options.mode === 'scroll_rush' || options.mode === 'capture_logo') && input.dashPressed && s.dashCooldownMs === 0) {
        s.dashMs = RULES.dashMs;
        s.dashCooldownMs = RULES.dashCooldownMs;
    }
    s.vx = s.dashMs > 0 ? s.facing * RULES.dashSpeed : input.axisX * (options.seeker ? RULES.seekerSpeed : RULES.speed) * (options.speedScale ?? 1);
    if (s.impulseX !== undefined) {
        s.vx += s.impulseX;
        s.impulseX = Math.sign(s.impulseX) * Math.max(0, Math.abs(s.impulseX) - 900 * dt);
        if (Math.abs(s.impulseX) < 0.01)
            delete s.impulseX;
    }
    s.vy = s.dashMs > 0 ? 0 : s.vy + RULES.gravity * dt;
    // Axis-separated swept collision prevents crossing thin solids/platforms at dash speed.
    const oldX = s.x;
    const horizontalVelocity = s.vx;
    s.x += horizontalVelocity * dt;
    // Bounds are scalars, with the identical bodyBounds arithmetic grouping.
    // y is invariant in this horizontal pass; x must still be reread after each
    // contact so collider-order behavior remains exactly the same as before.
    const horizontalTop = s.y - RULES.bodyHeight;
    const horizontalBottom = horizontalTop + RULES.bodyHeight;
    const oldLeft = oldX - RULES.bodyWidth / 2;
    const oldRight = oldLeft + RULES.bodyWidth;
    for (const c of horizontalColliders) {
        if (c.kind !== 'solid')
            continue;
        const b = c.bounds;
        const afterLeft = s.x - RULES.bodyWidth / 2;
        const afterRight = afterLeft + RULES.bodyWidth;
        if (horizontalTop >= b.y + b.height || horizontalBottom <= b.y)
            continue;
        if (horizontalVelocity > 0 && oldRight <= b.x && afterRight >= b.x) {
            s.x = Math.min(s.x, b.x - RULES.bodyWidth / 2);
            s.vx = 0;
        }
        else if (horizontalVelocity < 0 && oldLeft >= b.x + b.width && afterLeft <= b.x + b.width) {
            s.x = Math.max(s.x, b.x + b.width + RULES.bodyWidth / 2);
            s.vx = 0;
        }
    }
    if (options.horizontalBounds !== false)
        s.x = Math.max(RULES.bodyWidth / 2, Math.min(map.world.width - RULES.bodyWidth / 2, s.x));
    const oldY = s.y;
    const verticalVelocity = s.vy;
    s.y += verticalVelocity * dt;
    s.grounded = false;
    // x is invariant in the vertical pass; preserve (x-half)+width rather
    // than simplifying to x+half, which could change floating-point rounding.
    const verticalLeft = s.x - RULES.bodyWidth / 2;
    const verticalRight = verticalLeft + RULES.bodyWidth;
    for (const c of verticalCandidates ? verticalCandidates(oldY, s.y, verticalVelocity) : map.colliders) {
        const b = c.bounds;
        if (verticalLeft >= b.x + b.width || verticalRight <= b.x)
            continue;
        if (verticalVelocity >= 0 && oldY <= b.y + 0.01 && s.y >= b.y) {
            s.y = Math.min(s.y, b.y);
            s.vy = 0;
            s.grounded = true;
            s.airJumps = 1;
            s.coyoteMs = RULES.coyoteMs;
        }
        else if (c.kind === 'solid' && verticalVelocity < 0 && oldY - RULES.bodyHeight >= b.y + b.height - 0.01 && s.y - RULES.bodyHeight <= b.y + b.height) {
            s.y = Math.max(s.y, b.y + b.height + RULES.bodyHeight);
            s.vy = 0;
        }
    }
    if (s.y < RULES.bodyHeight) {
        s.y = RULES.bodyHeight;
        s.vy = Math.max(0, s.vy);
    }
    return s;
}
/** Query-owned broad phase. Surface buckets only omit boundaries outside the
 * swept vertical interval; exact predicates still run in original input order. */
function verticalSurfaceCandidates(colliders) {
    const size = 64;
    const build = (up) => {
        const indexes = new Map();
        colliders.forEach((collider, index) => {
            if (up && collider.kind !== 'solid')
                return;
            const y = up ? collider.bounds.y + collider.bounds.height : collider.bounds.y;
            const key = Math.floor(y / size);
            const list = indexes.get(key) ?? [];
            list.push(index);
            indexes.set(key, list);
        });
        const singles = new Map([...indexes].map(([key, list]) => [key, list.map(index => colliders[index])]));
        const combinations = new Map();
        return { indexes, singles, combinations };
    };
    const down = build(false), up = build(true), empty = [];
    return (oldY, newY, velocity) => {
        // One pixel is a conservative broad-phase margin, not a collision tolerance.
        // It contains the existing .01 contact terms without reversing floating-point
        // arithmetic such as oldY <= surfaceY + .01 into a stricter subtraction.
        const headOffset = velocity < 0 ? RULES.bodyHeight : 0;
        const first = Math.floor((Math.min(oldY - headOffset, newY - headOffset) - 1) / size);
        const last = Math.floor((Math.max(oldY - headOffset, newY - headOffset) + 1) / size);
        if (!Number.isFinite(first) || !Number.isFinite(last) || last - first > 3)
            return colliders;
        const table = velocity < 0 ? up : down;
        if (first === last)
            return table.singles.get(first) ?? empty;
        const key = first * 4 + (last - first);
        const cached = table.combinations.get(key);
        if (cached)
            return cached;
        const selected = [];
        for (let band = first; band <= last; band++)
            selected.push(...(table.indexes.get(band) ?? []));
        selected.sort((a, b) => a - b);
        const result = selected.map(index => colliders[index]);
        if (table.combinations.size < 64)
            table.combinations.set(key, result);
        return result;
    };
}
/**
 * Bounded compiler reachability using the exact shared movement step.
 * It checks a small deterministic set of ordinary jump/double-jump/dash
 * timings; it never drives a rendered page or player session.
 */
export function platformReachable(from, to, map, mode, options = {}) {
    const deltaX = to.x - from.x;
    const rise = from.y - to.y;
    const drop = to.y - from.y;
    const jumpRise = (RULES.jumpSpeed * RULES.jumpSpeed) / RULES.gravity;
    const speedScale = options.speedScale ?? 1;
    if (!Number.isFinite(speedScale) || speedScale <= 0 || speedScale > 2)
        return false;
    const dashAllowed = options.dashAllowed ?? (mode === 'scroll_rush' || mode === 'capture_logo');
    const dashBonus = dashAllowed ? Math.max(0, RULES.dashSpeed - RULES.speed * speedScale) * RULES.dashMs / 1000 : 0;
    const coarseHorizontalLimit = RULES.speed * speedScale * 2.4 + dashBonus + RULES.bodyWidth;
    if (Math.abs(deltaX) > coarseHorizontalLimit || rise > jumpRise + 16 || drop > 1_300)
        return false;
    const horizontalMin = Math.min(from.x, to.x) - RULES.bodyWidth * 2;
    const horizontalMax = Math.max(from.x, to.x) + RULES.bodyWidth * 2;
    const verticalMin = Math.min(from.y, to.y) - jumpRise - RULES.bodyHeight;
    const verticalMax = Math.max(from.y, to.y) + RULES.bodyHeight;
    const nearbyColliders = map.colliders.filter(c => c.bounds.x + c.bounds.width >= horizontalMin
        && c.bounds.x <= horizontalMax && c.bounds.y + c.bounds.height >= verticalMin && c.bounds.y <= verticalMax);
    const localMap = { world: map.world, colliders: nearbyColliders };
    const horizontalColliders = nearbyColliders.filter(c => c.kind === 'solid');
    const verticalCandidates = verticalSurfaceCandidates(nearbyColliders);
    if (Math.abs(from.y - to.y) <= 2) {
        const sameContinuousSurface = nearbyColliders.some(c => c.bounds.x <= Math.min(from.x, to.x)
            && c.bounds.x + c.bounds.width >= Math.max(from.x, to.x)
            && Math.abs(c.bounds.y - from.y) <= 2 && Math.abs(c.bounds.y - to.y) <= 2);
        if (sameContinuousSurface && hasLineOfSight(from, to, localMap))
            return true;
    }
    const direction = Math.sign(deltaX);
    const estimatedFrames = Math.max(1, Math.round(Math.abs(deltaX) / RULES.speed / (RULES.tickMs / 1000)));
    const stops = [
        Math.max(0, estimatedFrames - 10),
        estimatedFrames,
        estimatedFrames + 10,
    ].filter((frame, index, list) => frame <= 130 && list.indexOf(frame) === index);
    const schedules = [
        { firstJump: 0, secondJump: null, dash: null },
        { firstJump: 0, secondJump: 20, dash: null },
        { firstJump: 0, secondJump: 28, dash: null },
        { firstJump: 8, secondJump: 30, dash: null },
        { firstJump: null, secondJump: null, dash: null },
    ];
    if (dashAllowed) {
        schedules.push({ firstJump: 0, secondJump: 20, dash: 12 }, { firstJump: 0, secondJump: 28, dash: 20 }, { firstJump: 8, secondJump: 30, dash: 12 }, { firstJump: null, secondJump: null, dash: Math.max(0, estimatedFrames - 5) });
    }
    // Query-owned objects never escape into runtime/prediction or caller state.
    const movementOptions = { mode, speedScale, dashAllowed, horizontalBounds: true };
    const input = { sequence: 0, axisX: 0, jumpPressed: false, dashPressed: false };
    const targetBand = { x: to.x - RULES.bodyWidth / 2, y: to.y - 1, width: RULES.bodyWidth, height: 2 };
    const maxFrames = drop > jumpRise ? 150 : 120;
    for (const schedule of schedules) {
        for (const stopFrame of stops) {
            let state = { ...createMovementState(from), grounded: true, coyoteMs: RULES.coyoteMs };
            for (let frame = 0; frame < maxFrames; frame++) {
                input.sequence = frame;
                input.axisX = frame < stopFrame ? direction : 0;
                input.jumpPressed = frame === schedule.firstJump || frame === schedule.secondJump;
                input.dashPressed = frame === schedule.dash;
                state = stepMovementWithSolids(state, input, localMap, RULES.tickMs, movementOptions, horizontalColliders, true, verticalCandidates);
                if (state.grounded && frame > 0) {
                    const body = bodyBounds(state);
                    if (Math.abs(state.y - to.y) <= 2 && intersects(body, targetBand))
                        return true;
                    // After the ordinary target check, a stopped grounded query with no
                    // future input has an invariant position. Gravity re-lands on the same
                    // support in the same collider order; other overlapping supports cannot
                    // move that minimum landing downward. Exclude the final top clamp, which
                    // can move a contact away from its supporting surface.
                    const jumpPending = [schedule.firstJump, schedule.secondJump].some((frameAt) => frameAt !== null && frame < frameAt);
                    const dashPending = schedule.dash !== null && frame < schedule.dash;
                    if (frame >= stopFrame && state.vx === 0 && state.vy === 0 && state.dashMs === 0
                        && state.y > RULES.bodyHeight && !jumpPending && !dashPending) {
                        // A surface just above the landing may enter the existing .01 contact
                        // allowance on the next frame even if it missed the previous oldY.
                        // Keep simulating that case; only an unchanged landing is absorbing.
                        const left = state.x - RULES.bodyWidth / 2, right = left + RULES.bodyWidth;
                        const canSettleHigher = nearbyColliders.some(c => left < c.bounds.x + c.bounds.width && right > c.bounds.x
                            && c.bounds.y < state.y && state.y <= c.bounds.y + 0.01);
                        if (!canSettleHigher)
                            break;
                    }
                    if (schedule.firstJump === null || jumpPending)
                        continue;
                    // The route graph can continue from a different landing surface as a new edge.
                    break;
                }
            }
        }
    }
    return false;
}
export function createRushState(map) { if (map.setup.mode !== 'scroll_rush')
    throw new Error('Scroll Rush map required'); return { movement: createMovementState(map.setup.spawn), checkpoint: 0, penaltyMs: 0, elapsedMs: 0, finished: false }; }
export function stepRush(state, input, map, dtMs = RULES.tickMs) {
    if (map.setup.mode !== 'scroll_rush')
        throw new Error('Scroll Rush map required');
    if (state.finished)
        return state;
    const s = { ...state, movement: stepMovement(state.movement, input, map, dtMs, { mode: 'scroll_rush' }), elapsedMs: state.elapsedMs + dtMs };
    const checkpoint = map.setup.checkpoints[s.checkpoint];
    if (checkpoint && intersects(bodyBounds(s.movement), checkpoint))
        s.checkpoint++;
    if (s.movement.y > map.world.height + 100) {
        const cp = map.setup.checkpoints[s.checkpoint - 1];
        s.movement = createMovementState(cp ? { x: cp.x + cp.width / 2, y: cp.y + cp.height } : map.setup.spawn);
        s.penaltyMs += RULES.fallPenaltyMs;
    }
    if (s.checkpoint === map.setup.checkpoints.length && intersects(bodyBounds(s.movement), map.setup.finish))
        s.finished = true;
    return s;
}
export function hasLineOfSight(a, b, map) {
    // Segment/AABB slab intersection. One-way platforms are not occluding walls.
    for (const c of map.colliders) {
        if (c.kind !== 'solid')
            continue;
        const r = c.bounds;
        let lo = 0, hi = 1;
        let hit = true;
        for (const axis of ['x', 'y']) {
            const d = b[axis] - a[axis];
            const min = r[axis], max = min + (axis === 'x' ? r.width : r.height);
            if (Math.abs(d) < 1e-9) {
                if (a[axis] < min || a[axis] > max) {
                    hit = false;
                    break;
                }
            }
            else {
                let t1 = (min - a[axis]) / d, t2 = (max - a[axis]) / d;
                if (t1 > t2)
                    [t1, t2] = [t2, t1];
                lo = Math.max(lo, t1);
                hi = Math.min(hi, t2);
                if (lo > hi) {
                    hit = false;
                    break;
                }
            }
        }
        if (hit && lo > 0.001 && lo < 0.999)
            return false;
    }
    return true;
}
export function applyImpulse(state, impulseX, lift) { return { ...state, impulseX: Math.max(-1000, Math.min(1000, (state.impulseX ?? 0) + impulseX)), vy: Math.min(state.vy, lift), grounded: false, coyoteMs: 0, dashMs: 0 }; }
export function activeColliders(map, disabledIds) { return map.colliders.filter(c => !disabledIds.has(c.id)); }
export function withChallengeSetup(map, optionId) { if (map.setup.mode !== 'scroll_rush')
    throw new Error('INVALID_CHALLENGE'); const option = map.setup.challengeOptions?.find(o => o.id === optionId); if (!option)
    throw new Error('INVALID_CHALLENGE'); return { ...map, setup: { ...map.setup, spawn: option.spawn, checkpoints: option.checkpoints, finish: option.finish } }; }
//# sourceMappingURL=index.js.map